import { resolveArtworkUrl, CONDITION_COLORS, LOCATION_COLORS, COLOUR_SWATCHES, EMPTY_STATE_IMG } from "@/lib/api";
import { Disc3, Plus, BadgeCheck, Zap, Droplets, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";

function VinylCard({ record, onSelect, index, cleaningDue, priceRise }) {
    return (
        <button
            onClick={() => onSelect(record)}
            className="tactile-card rounded-md overflow-hidden text-left fade-up group"
            style={{ animationDelay: `${Math.min(index * 30, 300)}ms` }}
            data-testid={`vinyl-card-${record.id}`}
        >
            <div className="aspect-square bg-secondary overflow-hidden relative">
                <img
                    src={resolveArtworkUrl(record)}
                    alt={`${record.album_title} by ${record.artist}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                />
                <div className="absolute top-3 right-3 flex gap-2">
                    <span className={`px-2.5 py-1 text-xs rounded-full border backdrop-blur-md ${LOCATION_COLORS[record.location] || LOCATION_COLORS["Andy"]} bg-white/80`}>
                        {record.location}
                    </span>
                </div>
                {record.limited && (
                    <div
                        className="absolute bottom-3 right-3 inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded-full bg-[#C0392B] text-white backdrop-blur-md border border-white/10 shadow-sm"
                        data-testid={`limited-badge-${record.id}`}
                    >
                        <Zap className="w-3 h-3" strokeWidth={2.5} />
                        Limited
                    </div>
                )}
                {record.discogs_id && (
                    <div
                        className="absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono rounded-full bg-foreground/85 text-background backdrop-blur-md border border-white/10"
                        title={`Linked to Discogs #${record.discogs_id}`}
                        data-testid={`discogs-badge-${record.id}`}
                    >
                        <BadgeCheck className="w-3 h-3" strokeWidth={2} />
                        DISCOGS
                    </div>
                )}
            </div>
            <div className="p-4 space-y-2">
                <p className="overline text-muted-foreground">{record.artist}</p>
                <h3 className="font-display font-semibold text-lg leading-tight line-clamp-2 tracking-tight">
                    {record.album_title}
                </h3>
                <div className="flex flex-wrap gap-1.5 pt-1">
                    <span className={`px-2 py-0.5 text-[10px] rounded-full border ${CONDITION_COLORS[record.media_condition]}`}>
                        M: {record.media_condition}
                    </span>
                    <span className={`px-2 py-0.5 text-[10px] rounded-full border ${CONDITION_COLORS[record.sleeve_condition]}`}>
                        S: {record.sleeve_condition}
                    </span>
                    <span className="px-2 py-0.5 text-[10px] rounded-full border border-border bg-muted text-muted-foreground font-mono">
                        {record.speed} rpm
                    </span>
                    {record.colour && record.colour !== "Black" && (
                        <span className="px-2 py-0.5 text-[10px] rounded-full border border-border bg-card inline-flex items-center gap-1">
                            <span
                                className="inline-block w-2 h-2 rounded-full border border-border/60"
                                style={{ background: COLOUR_SWATCHES[record.colour] || "#111" }}
                            />
                            {record.colour}
                        </span>
                    )}
                    {cleaningDue && (
                        <span className="px-2 py-0.5 text-[10px] rounded-full border border-sky-200 bg-sky-50 text-sky-800 inline-flex items-center gap-1"
                            data-testid={`clean-due-tag-${record.id}`}>
                            <Droplets className="w-3 h-3" /> Clean due
                        </span>
                    )}
                    {priceRise && (
                        <span className="px-2 py-0.5 text-[10px] rounded-full border border-emerald-200 bg-emerald-50 text-emerald-800 inline-flex items-center gap-1"
                            title={`Discogs value £${priceRise.previous} → £${priceRise.current}`}
                            data-testid={`value-up-tag-${record.id}`}>
                            <TrendingUp className="w-3 h-3" /> Value up {priceRise.pct}%
                        </span>
                    )}
                </div>
            </div>
        </button>
    );
}

function EmptyState({ onAdd }) {
    return (
        <div className="tactile-card rounded-md p-12 text-center" data-testid="empty-state">
            <img
                src={EMPTY_STATE_IMG}
                alt=""
                className="w-32 h-32 object-cover rounded-full mx-auto mb-6 grayscale opacity-80"
            />
            <h3 className="font-display text-3xl font-bold tracking-tight">A silent shelf.</h3>
            <p className="text-muted-foreground mt-3 max-w-md mx-auto">
                Your collection is empty. Add your first record to start the archive.
            </p>
            <Button
                onClick={onAdd}
                className="mt-6 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground"
                data-testid="empty-state-add-btn"
            >
                <Plus className="w-4 h-4 mr-2" />
                Add your first record
            </Button>
        </div>
    );
}

function LoadingState() {
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4 sm:gap-6 lg:gap-8" data-testid="loading-state">
            {[...Array(8)].map((_, i) => (
                <div key={i} className="tactile-card rounded-md overflow-hidden">
                    <div className="aspect-square bg-muted animate-pulse" />
                    <div className="p-4 space-y-2">
                        <div className="h-3 bg-muted rounded animate-pulse w-1/3" />
                        <div className="h-5 bg-muted rounded animate-pulse w-3/4" />
                    </div>
                </div>
            ))}
        </div>
    );
}

export default function VinylGrid({ vinyls, loading, onSelect, onAdd, alertFlags }) {
    if (loading) return <LoadingState />;
    if (vinyls.length === 0) return <EmptyState onAdd={onAdd} />;

    return (
        <section
            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4 sm:gap-6 lg:gap-8"
            data-testid="vinyl-grid"
        >
            {vinyls.map((v, i) => (
                <VinylCard key={v.id} record={v} onSelect={onSelect} index={i}
                    cleaningDue={alertFlags?.cleaning.has(v.id)} priceRise={alertFlags?.price[v.id]} />
            ))}
        </section>
    );
}
